import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowUpRightFromSquare,
  faBolt,
  faBox,
  faCircleInfo,
  faDollarSign,
  faInbox,
  faPlus,
  faStar,
  faTruck
} from '@fortawesome/free-solid-svg-icons';
import { TabsModule } from 'primeng/tabs';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';
import { SkeletonModule } from 'primeng/skeleton';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService } from '../../services/language.service';

export interface MatchAction {
  label: string;
  styleClass?: string;
}

export interface MatchAvatar {
  image?: string;
  label?: string;
  styleClass?: string;
}

export interface MatchItem {
  id: number;
  matchNumber: string;
  loadRequestNumber?: string;
  loadAvailabilityNumber?: string;
  status: string;
  distanceAway: string;
  eta: string;
  offerAmount: string;
  ratePerMile: string;
  badges: string[];
  equipment: string[];
  preferred?: boolean;
  avatar?: MatchAvatar;
  actions: MatchAction[];
}

export interface MatchGroup {
  key: string;
  label: string;
  matches: MatchItem[];
  summaryLabel?: string;
  description?: string;
}

interface SummaryItem {
  label: string;
  count: number;
}

@Component({
  selector: 'app-matches-list',
  standalone: true,
  imports: [
    CommonModule,
    TabsModule,
    BadgeModule,
    ButtonModule,
    AvatarModule,
    TagModule,
    ChipModule,
    SkeletonModule,
    TranslateModule,
    FontAwesomeModule
  ],
  templateUrl: './matches-list.component.html',
  styles: [`
    .matches-list-shell {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
      padding: 1rem;
      min-height: 100%;
    }

    .icon-button fa-icon {
      margin-inline-end: 0.45rem;
    }

    fa-icon {
      color: currentColor;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MatchesListComponent {
  public readonly languageService = inject(LanguageService);

  readonly icons = {
    box: faBox,
    bolt: faBolt,
    dollar: faDollarSign,
    external: faArrowUpRightFromSquare,
    inbox: faInbox,
    info: faCircleInfo,
    plus: faPlus,
    star: faStar,
    truck: faTruck
  };

  matchGroups = input<MatchGroup[]>([]);
  loading = input<boolean>(false);
  actionClick = output<{ match: MatchItem, action: MatchAction }>();

  activeKey?: string;

  summaryItems = computed<SummaryItem[]>(() => {
    return this.matchGroups()
      .filter(group => group.summaryLabel)
      .map(group => ({
        label: group.summaryLabel ?? group.label,
        count: group.matches.length
      }));
  });

  // Handle initial active key
  protected readonly initialKey = computed(() => {
    const groups = this.matchGroups();
    return groups.length > 0 ? groups[0].key : undefined;
  });

  getStatusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' | undefined {
    switch (status?.toUpperCase()) {
      case 'BOOKED':
      case 'AGREED':
        return 'success';
      case 'PROPOSAL':
      case 'NEGOTIATING':
        return 'info';
      case 'AWAITING_CONFIRMATION':
        return 'warn';
      case 'REJECTED':
      case 'CANCELLED':
      case 'EXPIRED':
        return 'danger';
      default:
        return 'secondary';
    }
  }
}
